/**
 * AccordionItem Component
 */

import React, { FC, MouseEvent, useState, useRef } from 'react';
import { withTheme } from 'styled-components';
import {
  AccordionItemProps,
  AccordionIconSizeTypes,
  AccordionIconTypeTypes,
  AccordionIconThemeTypes,
} from './AccordionItem.types';
import Icon from '../Icon/icon';
import {
  Content,
  Button,
  HeadLine,
  SubHeader,
  SubHeading,
  IconWrapper,
  sizeCircularMap,
  sizeStandardMap,
  standardArrowColorMap,
  circularArrowColorMap
} from './AccordionItem.style';

const AccordionItem: FC<AccordionItemProps> = ({
  headline,
  children,
  render,
  isOpen,
  iconSize = AccordionIconSizeTypes.LG,
  iconType = AccordionIconTypeTypes.STANDARD,
  showIcon = true,
  contentId,
  initOpen = false,
  handlers = {},
  subHeaderText = '',
  theme = {
    dark: false
  },
  ...otherProps
}: AccordionItemProps) => {

  /** Icon theme */
  const iconTheme = theme.dark ? AccordionIconThemeTypes.LIGHT : AccordionIconThemeTypes.DARK;

  /** Internal private state */
  const [subHeaderPrivateValue, setSubHeaderPrivateValue] = useState('');
  const [isOpenPrivate, setIsOpenPrivate] = useState(initOpen);

  /** Check if the component is stateless or state full */
  const isStateless = useRef(isOpen !== undefined);

  /** Local variables, no matter if stateless or stateful */
  const isAccordionOpened = isStateless.current ? isOpen : isOpenPrivate;
  const subHeaderValue = subHeaderText || subHeaderPrivateValue;

  /** Content id, unique for aria controls purposes */
  const contentIdPrefix: string = 'Accordion-component-content';
  const uniqueContentId: string = `${contentIdPrefix}-${contentId}`;

  const iconFill: string = iconType === AccordionIconTypeTypes.CIRCULAR
    ? circularArrowColorMap.arrowColor[iconTheme]
    : standardArrowColorMap.arrowColor[iconTheme];

  const iconWidth: number = iconType === AccordionIconTypeTypes.CIRCULAR
    ? sizeCircularMap[iconSize]
    : sizeStandardMap[iconSize];

  const onClick: (event: MouseEvent) => void = (event: MouseEvent) => {
    const defaultHandler: Function = () => null;
    const {onOpen = defaultHandler, onClose = defaultHandler} =  handlers;
    if (isAccordionOpened) {
      onClose(event);
    } else {
      onOpen(event);
    }
    if (!isStateless.current){
      setIsOpenPrivate(!isAccordionOpened);
    }
  };

  /** TODO: Evaluate to use useCallback */
  const updateSubHeader: (t: string) => void = (subHeader: string) => {
    setSubHeaderPrivateValue(subHeader);
  };

  return (
    <>
      <Button
          onClick={onClick}
          aria-expanded={isAccordionOpened}
          aria-controls={uniqueContentId}
          {...otherProps}
        >
        <HeadLine>{ headline }</HeadLine>
        <SubHeading>
          <SubHeader>{ !isAccordionOpened && subHeaderValue }</SubHeader>
          {
            showIcon &&
            <IconWrapper iconType={iconType} iconTheme={iconTheme} iconRotated={isAccordionOpened}>
              <Icon name='arrow-expand' fill={iconFill} width={iconWidth}/>
            </IconWrapper>
          }
        </SubHeading>
      </Button>
      <Content
        id={uniqueContentId}
        isAccordionOpened={isAccordionOpened!}
        theme={theme}
      >
        { render && render(updateSubHeader) }
        { children }
      </Content>
    </>
  );
};

export default withTheme(AccordionItem);
